/**
 * 10b_meditation_body.tsx
 * Breath-awareness meditation — a calm bridge between Pranayama and Yoga Nidra.
 * Body-only version for PracticeSwiper. Matches slides 8–11 house style.
 *
 * Differs from the pranayama slides on purpose: breath AWARENESS means observing
 * the natural breath, so there is no counting and no INHALE/HOLD/EXHALE bar —
 * just a slow, even, hypnotic orb to rest attention on. Runs a fixed ~2 minutes,
 * then dispatches yogyism:next to auto-advance to Yoga Nidra.
 */

import React, { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import ScaleToFit from "./ScaleToFit";
import SideStarField from "./SideStarField";
import { useIsPortrait } from "./useIsPortrait";
import { useMode } from "./modeBus";
import MeditationMobile from "./MeditationMobile";
import { FONT_BODY, FONT_DISPLAY, FONT_MONO } from "theme/fonts";
import { pick } from "i18n/lang";
import { localAudio } from "i18n/audio";
import { meditationContent, useMeditation, Seg } from "./meditation.data";

const accent = "#4bb8b0";
const font   = FONT_BODY;

if (typeof document !== "undefined" && !document.getElementById("yogyism-pulse-4bb8b0")) {
 const s = document.createElement("style");
 s.id = "yogyism-pulse-4bb8b0";
 s.textContent = `
   @keyframes yogyismPulse_4bb8b0 {
     0%   { box-shadow: 0 0 0 0 rgba(75,184,176,0.70), 0 0 12px 2px rgba(75,184,176,0.45); transform: scale(1); }
     50%  { box-shadow: 0 0 0 16px rgba(75,184,176,0), 0 0 20px 6px rgba(75,184,176,0.65); transform: scale(1.12); }
     100% { box-shadow: 0 0 0 0 rgba(75,184,176,0), 0 0 12px 2px rgba(75,184,176,0.45); transform: scale(1); }
   }`;
 document.head.appendChild(s);
}

const S: Record<string, CSSProperties> = {
 root:    { background:"#040f18", fontFamily:font, color:"#d0e8f8", width:"100%", height:"100%", display:"flex", flexDirection:"column", boxSizing:"border-box", overflow:"hidden" },
 subtitleBar: {
   background: "#050f1a", borderBottom: "1px solid rgba(75,184,176,0.12)",
   padding: "clamp(5px,0.6vw,9px) clamp(16px,3vw,40px)",
   display: "flex", alignItems: "center", justifyContent: "space-between",
   gap: "clamp(12px,2vw,28px)", flexShrink: 0,
 },
 body:    { flex:1, minHeight:0, display:"flex", gap:"clamp(16px,2vw,28px)", padding:"clamp(10px,1.4vw,18px) clamp(16px,3vw,40px) clamp(10px,1.2vw,14px)", alignItems:"stretch", overflow:"hidden" },
 content: { flex:1, minWidth:"min(100%, 380px)", display:"flex", flexDirection:"column", justifyContent:"space-between" },
 panel:   { width:"clamp(220px,28vw,380px)", flexShrink:0, display:"flex", flexDirection:"column", alignItems:"center", overflow:"hidden" },
};

/* ─── Meditation Guide Canvas ────────────────────────────────────────────── */
const MeditationGuideInner: React.FC<{ playing: boolean; onComplete?: () => void; }> = ({ playing, onComplete }) => {
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const st = useRef({
    animId:         0,
    playing:        false,
    startMs:        0,
    introTimeoutId: 0,
    practiceAudio:  null as HTMLAudioElement | null,
    introAudio:     null as HTMLAudioElement | null,
    drawAnim:       null as (() => void) | null,
    drawStatic:     null as (() => void) | null,
    stopAudio:      null as (() => void) | null,
    startWithIntro: null as (() => void) | null,
  });

  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d") as CanvasRenderingContext2D; if (!ctx) return;
    const tx = pick(meditationContent).canvas; // slides remount on language change
    const W = 400, H = 560; canvas.width = W; canvas.height = H;
    const cx = 200, cy = 240;
    const R_MIN = 55, R_MAX = 100;
    const TOTAL_ROUNDS = 12;         // whole breath cycles (≈2 min at a 10s cycle) — ends on an exhale
    let   cycleSecs = 10;            // one breath cycle; set from the audio once loaded

    const stars = [[22,20,0.9],[78,12,0.7],[318,18,1.0],[375,42,0.7],[38,68,0.8],[355,62,0.9],[390,10,0.9],[115,44,0.7],[15,110,0.7],[345,105,0.8]];
    function easeInOut(t: number) { return t < 0.5 ? 2*t*t : -1+(4-2*t)*t; }

    // Narration plays once at the start; the drone (Web Audio, in the panel) carries
    // Practice audio is ONE breath cycle ("breathe in … and breathe out"), looped
    // for the full 2 minutes. The orb's breath is driven from its currentTime, so the
    // spoken "breathe in / out" always matches the orb — important with eyes closed.
    const introAudio    = localAudio("/audio/intro-meditation.mp3");  introAudio.preload = "auto";
    const practiceAudio = localAudio("/audio/meditation-practice.mp3"); practiceAudio.preload = "auto";
    practiceAudio.loop = true;
    practiceAudio.addEventListener("loadedmetadata", () => {
      if (isFinite(practiceAudio.duration) && practiceAudio.duration > 0) cycleSecs = practiceAudio.duration;
    });
    st.current.introAudio    = introAudio;
    st.current.practiceAudio = practiceAudio;

    function stopAllAudio() {
      introAudio.onended = null;
      try { introAudio.pause(); introAudio.currentTime = 0; } catch (_) {}
      try { practiceAudio.pause(); practiceAudio.currentTime = 0; } catch (_) {}
    }

    function drawBg() {
      ctx.clearRect(0, 0, W, H); ctx.fillStyle = "#040f18"; ctx.fillRect(0, 0, W, H);
      stars.forEach(([x, y, r]) => { ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI*2); ctx.fillStyle = "rgba(255,255,255,0.50)"; ctx.fill(); });
      ctx.beginPath(); ctx.arc(368,36,18,0,Math.PI*2); ctx.fillStyle="rgba(224,250,248,0.07)"; ctx.fill();
      ctx.beginPath(); ctx.arc(368,36, 8,0,Math.PI*2); ctx.fillStyle="#eafffb"; ctx.fill();
    }

    // phase 0..1 through one breath; cosmetic = free-running counter for shimmer/ripples
    function drawScene(breathPhase: number, elapsedFrac: number, cosmetic: number, roundsDone: number = 0) {
      const isInhale = breathPhase < 0.5;
      const half     = isInhale ? breathPhase / 0.5 : (breathPhase - 0.5) / 0.5;
      const radius   = isInhale
        ? R_MIN + (R_MAX - R_MIN) * easeInOut(half)
        : R_MAX - (R_MAX - R_MIN) * easeInOut(half);
      const breathFill = (radius - R_MIN) / (R_MAX - R_MIN);

      const haloR = radius + 90;
      const halo = ctx.createRadialGradient(cx, cy, radius * 0.4, cx, cy, haloR);
      halo.addColorStop(0,   `rgba(75,184,176,${(0.14 + 0.20 * breathFill).toFixed(3)})`);
      halo.addColorStop(0.6, `rgba(75,184,176,${(0.05 + 0.07 * breathFill).toFixed(3)})`);
      halo.addColorStop(1,   "rgba(75,184,176,0)");
      ctx.beginPath(); ctx.arc(cx, cy, haloR, 0, Math.PI*2); ctx.fillStyle = halo; ctx.fill();

      for (let i = 0; i < 5; i++) {
        const rp = ((cosmetic * 0.4 + i * 20) % 100) / 100;
        const rr = radius + 4 + rp * 80;
        const ra = (1 - rp) * (0.12 + 0.16 * breathFill);
        ctx.beginPath(); ctx.arc(cx, cy, rr, 0, Math.PI*2);
        ctx.strokeStyle = `rgba(75,184,176,${ra.toFixed(3)})`; ctx.lineWidth = 1.2; ctx.stroke();
      }

      const grd = ctx.createRadialGradient(cx - radius*0.3, cy - radius*0.3, 2, cx, cy, radius);
      grd.addColorStop(0,    `rgba(200,244,240,${(0.42 + 0.22 * breathFill).toFixed(3)})`);
      grd.addColorStop(0.45, `rgba(75,184,176,${(0.26 + 0.12 * breathFill).toFixed(3)})`);
      grd.addColorStop(1,    "rgba(75,184,176,0.05)");
      ctx.beginPath(); ctx.arc(cx,cy,radius,0,Math.PI*2); ctx.fillStyle=grd; ctx.fill();

      const sa = (cosmetic * 0.015) % (Math.PI*2);
      ctx.beginPath(); ctx.arc(cx, cy, radius*0.82, sa, sa + Math.PI*0.6);
      ctx.strokeStyle = `rgba(205,246,242,${(0.16 + 0.16 * breathFill).toFixed(3)})`; ctx.lineWidth = 2; ctx.stroke();

      ctx.strokeStyle = `rgba(75,184,176,${(0.68 + 0.22 * breathFill).toFixed(3)})`;
      ctx.lineWidth = 2.2; ctx.stroke();

      // Phase label inside the orb (like the pranayama slides), but no count number
      ctx.textAlign = "center";
      ctx.font = "bold 19px 'JetBrains Mono','Courier New',monospace";
      ctx.fillStyle = `rgba(75,184,176,${(0.72 + 0.22 * breathFill).toFixed(3)})`;
      ctx.fillText(isInhale ? tx.inhale : tx.exhale, cx, cy + 6);

      // gentle guiding line beneath the orb
      ctx.font = "15px 'Space Grotesk',Arial,sans-serif";
      ctx.fillStyle = "rgba(75,184,176,0.40)";
      ctx.fillText(tx.guideLine, cx, cy + 132);

      // two-segment phase bar (INHALE | EXHALE), no counts
      const segs = [{ label: tx.inhale }, { label: tx.exhale }];
      const barY2 = cy + 172, barX2 = 40, barW2 = W - 80;
      segs.forEach((seg, i) => {
        const sx = barX2 + i * (barW2 / 2), sw = barW2 / 2;
        const active = (i === 0 && isInhale) || (i === 1 && !isInhale);
        ctx.fillStyle   = active ? "rgba(75,184,176,0.28)" : "rgba(75,184,176,0.07)"; ctx.fillRect(sx, barY2, sw - 2, 20);
        ctx.strokeStyle = active ? "rgba(75,184,176,0.55)" : "rgba(75,184,176,0.14)"; ctx.lineWidth = 1; ctx.strokeRect(sx, barY2, sw - 2, 20);
        ctx.font = "10px 'JetBrains Mono','Courier New',monospace"; ctx.fillStyle = active ? "#4bb8b0" : "rgba(75,184,176,0.30)";
        ctx.fillText(seg.label, sx + (sw - 2) / 2, barY2 + 13);
      });

      // slim progress line (elapsed of 2 min)
      const pX = 40, pY = cy + 200, pW = W - 80;
      ctx.fillStyle = "rgba(75,184,176,0.10)"; ctx.fillRect(pX, pY, pW, 3);
      const pG = ctx.createLinearGradient(pX, 0, pX + pW, 0);
      pG.addColorStop(0, "rgba(58,150,144,0.65)"); pG.addColorStop(1, "rgba(150,224,218,0.85)");
      ctx.fillStyle = pG; ctx.fillRect(pX, pY, pW * Math.min(1, elapsedFrac), 3);

      // round counter beneath the progress line
      ctx.textAlign = "center";
      ctx.font = "12px 'Space Grotesk',Arial,sans-serif";
      ctx.fillStyle = "rgba(75,184,176,0.55)";
      ctx.fillText(tx.breath(Math.min(roundsDone + 1, TOTAL_ROUNDS), TOTAL_ROUNDS), cx, pY + 20);
    }

    function drawStatic() {
      drawBg(); drawScene(0, 0, 0);
      ctx.fillStyle="rgba(4,15,24,0.40)"; ctx.fillRect(0,0,W,H);
      ctx.textAlign="center"; ctx.font="14px 'Space Grotesk',Arial,sans-serif";
      ctx.fillStyle="rgba(75,184,176,0.38)"; ctx.fillText(tx.pressPlay,cx,H-16);
    }

    function drawAnim() {
      if (!st.current.playing) return;
      drawBg();
      const now     = performance.now();
      const elapsed = now - st.current.startMs;
      const cyc     = (cycleSecs || 10) * 1000;
      // Breath phase runs on elapsed time so the orb always breathes, even before
      // the audio has loaded. cycleSecs is set from the audio once known, so the
      // orb's pace still matches the recorded "inhale / exhale".
      const roundsDone = Math.floor(elapsed / cyc);          // completed full cycles
      const phase      = (elapsed % cyc) / cyc;              // 0..1 within the current cycle
      const progress   = Math.min(1, (roundsDone + phase) / TOTAL_ROUNDS);
      drawScene(phase, progress, now / 16, roundsDone);

      // Finish only at the END of a full cycle (i.e. after a complete exhale),
      // never mid-inhale — so the session always closes cleanly.
      if (roundsDone >= TOTAL_ROUNDS) {
        st.current.playing = false;
        stopAllAudio();
        drawStatic();
        onCompleteRef.current?.();
        return;
      }
      st.current.animId = requestAnimationFrame(drawAnim);
    }

    function beginPractice() {
      clearTimeout(st.current.introTimeoutId);
      if (st.current.playing) return;
      st.current.playing = true;
      st.current.startMs = performance.now();
      const a = st.current.practiceAudio;
      if (a) { a.currentTime = 0; a.play().catch(() => {}); }   // narration once; drone carries the rest
      st.current.drawAnim?.();
    }

    function startWithIntro() {
      const intro = st.current.introAudio;
      if (!intro) { beginPractice(); return; }
      intro.onended = () => {
        intro.onended = null;
        st.current.introTimeoutId = window.setTimeout(beginPractice, 800);
      };
      intro.currentTime = 0;
      intro.play().then(() => {
        const dur = intro.duration;
        const ms  = isFinite(dur) ? Math.ceil(dur * 1000) + 2000 : 30000;
        st.current.introTimeoutId = window.setTimeout(beginPractice, ms);
      }).catch(beginPractice);
    }

    st.current.drawAnim       = drawAnim;
    st.current.drawStatic     = drawStatic;
    st.current.stopAudio      = stopAllAudio;
    st.current.startWithIntro = startWithIntro;
    drawStatic();

    const cur = st.current;
    return () => {
      cancelAnimationFrame(cur.animId);
      clearTimeout(cur.introTimeoutId);
      stopAllAudio();
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    const s = st.current;
    cancelAnimationFrame(s.animId);
    clearTimeout(s.introTimeoutId);
    s.playing = false;
    s.stopAudio?.();
    if (playing) { s.startWithIntro?.(); } else { s.drawStatic?.(); }
  }, [playing]);

  return (
    <canvas ref={canvasRef} style={{ display: "block", borderRadius: 8, width: "100%", height: "100%", objectFit: "contain" }} />
  );
};

const MeditationGuide: React.FC = () => {
 const [playing, setPlaying] = useState(false);
 const audioCtx=useRef<AudioContext|null>(null); const masterGain=useRef<GainNode|null>(null);
 const oscs=useRef<OscillatorNode[]>([]); const lfo=useRef<OscillatorNode|null>(null);
 const binOscs=useRef<OscillatorNode[]>([]); const binGain=useRef<GainNode|null>(null);

 const startAudio=()=>{ const ctx=new(window.AudioContext||(window as any).webkitAudioContext)(); audioCtx.current=ctx; const gain=ctx.createGain(); gain.gain.setValueAtTime(0,ctx.currentTime); gain.gain.linearRampToValueAtTime(0.04,ctx.currentTime+2.0); gain.connect(ctx.destination); masterGain.current=gain; const delay=ctx.createDelay(1.0); const fbG=ctx.createGain(); delay.delayTime.value=0.5; fbG.gain.value=0.32; delay.connect(fbG); fbG.connect(delay); delay.connect(gain); oscs.current=([[98,0.55],[147,0.26],[196,0.13]] as [number,number][]).map(([freq,vol])=>{ const osc=ctx.createOscillator(); const og=ctx.createGain(); osc.type="sine"; osc.frequency.value=freq; og.gain.value=vol; osc.connect(og); og.connect(delay); og.connect(gain); osc.start(); return osc; }); const lfoNode=ctx.createOscillator(); const lfoG=ctx.createGain(); lfoNode.frequency.value=0.06; lfoG.gain.value=0; lfoNode.connect(lfoG); lfoG.connect(gain.gain); lfoNode.start(); lfo.current=lfoNode; const merger=ctx.createChannelMerger(2); const bGain=ctx.createGain(); bGain.gain.setValueAtTime(0,ctx.currentTime); bGain.gain.linearRampToValueAtTime(0,ctx.currentTime+3.5); merger.connect(bGain); bGain.connect(ctx.destination); binGain.current=bGain; const lOsc=ctx.createOscillator(); const lG=ctx.createGain(); lOsc.type="sine"; lOsc.frequency.value=190; lG.gain.value=1; lOsc.connect(lG); lG.connect(merger,0,0); lOsc.start(); const rOsc=ctx.createOscillator(); const rG=ctx.createGain(); rOsc.type="sine"; rOsc.frequency.value=194; rG.gain.value=1; rOsc.connect(rG); rG.connect(merger,0,1); rOsc.start(); binOscs.current=[lOsc,rOsc]; };
 const stopAudio=()=>{ if(!audioCtx.current)return; const ctx=audioCtx.current; masterGain.current?.gain.linearRampToValueAtTime(0,ctx.currentTime+1.2); binGain.current?.gain.linearRampToValueAtTime(0,ctx.currentTime+1.2); setTimeout(()=>{ [...oscs.current,...binOscs.current].forEach(o=>{try{o.stop();}catch(_){}}); try{lfo.current?.stop();}catch(_){} ctx.close().catch(()=>{}); audioCtx.current=masterGain.current=lfo.current=binGain.current=null; oscs.current=binOscs.current=[]; },1300); };

 useEffect(()=>()=>{ [...oscs.current,...binOscs.current].forEach(o=>{try{o.stop();}catch(_){}}); try{lfo.current?.stop();}catch(_){} audioCtx.current?.close().catch(()=>{}); },[]);
 useEffect(()=>{ const stop=()=>{setPlaying(false);stopAudio();}; window.addEventListener("yogyism:stop",stop); return()=>window.removeEventListener("yogyism:stop",stop); },[]); // eslint-disable-line

 const handleToggle=()=>{ const next=!playing; setPlaying(next); if(next){startAudio(); window.dispatchEvent(new Event("yogyism:play"));}else{stopAudio();} };

 return (
   <div style={{display:"flex",flexDirection:"column",alignItems:"center",width:"100%",height:"100%"}}>
     <div style={{flexShrink:0,marginBottom:"clamp(4px,0.5vw,6px)"}}>
       <button onClick={handleToggle} style={{width:32,height:32,display:"flex",alignItems:"center",justifyContent:"center",background:playing?"rgba(75,184,176,0.08)":"rgba(75,184,176,0.32)",border:`1px solid rgba(75,184,176,${playing?0.32:0.85})`,borderRadius:"50%",cursor:"pointer",color:playing?"rgba(75,184,176,0.60)":accent,transition:"all 0.2s",animation:playing?"none":"yogyismPulse_4bb8b0 1.3s ease-in-out infinite"}} onMouseEnter={e=>(e.currentTarget.style.background="rgba(75,184,176,0.28)")} onMouseLeave={e=>(e.currentTarget.style.background=playing?"rgba(75,184,176,0.08)":"rgba(75,184,176,0.20)")}>
         {playing?<svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><rect x="0" y="0" width="10" height="10" rx="1"/></svg>:<svg width="9" height="10" viewBox="0 0 9 10" fill="currentColor"><polygon points="0,0 9,5 0,10"/></svg>}
       </button>
     </div>
     <div style={{flex:1,minHeight:0,width:"100%",overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center"}}>
       <MeditationGuideInner playing={playing} onComplete={() => {
         setPlaying(false);
         stopAudio();
         window.dispatchEvent(new Event("yogyism:next"));
       }}/>
     </div>
   </div>
 );
};


// ─── Slide content (tutorial-deck version) ───────────────────────────────────
const lbl: CSSProperties = {
  fontSize: "clamp(8px,0.7vw,11px)", fontFamily: FONT_MONO,
  color: accent, letterSpacing: 3, textTransform: "uppercase",
  marginBottom: "clamp(5px,0.6vw,8px)", fontStyle: "normal", flexShrink: 0,
};

const renderSegs = (segs: Seg[], boldColor: string) =>
  segs.map((s, i) =>
    s.b ? <span key={i} style={{ color: boldColor, fontWeight: 700 }}>{s.t}</span>
    : s.hl ? <span key={i} style={{ color: "#a8e6df" }}>{s.t}</span>
    : s.em ? <em key={i}>{s.t}</em>
    : <React.Fragment key={i}>{s.t}</React.Fragment>
  );

const MeditationInner: React.FC = () => {
 const { meta, technique, intro, stillness } = useMeditation();
 const fontLinkRef = useRef<HTMLLinkElement | null>(null);
 useEffect(() => {
   if (!document.querySelector('link[href*="Space+Grotesk"]')) {
     const link = document.createElement("link");
     link.rel  = "stylesheet";
     link.href = "https://fonts.googleapis.com/css2?family=Merriweather:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap";
     document.head.appendChild(link);
     fontLinkRef.current = link;
   }
   return () => {
     if (fontLinkRef.current && document.head.contains(fontLinkRef.current)) {
       document.head.removeChild(fontLinkRef.current); fontLinkRef.current = null;
     }
   };
 }, []);

 const rootRef = useRef<HTMLDivElement>(null);

 return (
<div style={S.root} ref={rootRef}>
<SideStarField rootRef={rootRef} />

    <div style={S.subtitleBar}>
      <div style={{ fontSize: "clamp(11px,1.0vw,14px)", fontStyle: "normal", color: "#7aa8cc", fontFamily: FONT_BODY }}>
        {meta.breadcrumbPre} &nbsp;·&nbsp;{" "}
        <span style={{ color: accent }}>{meta.breadcrumbAccent}</span>
      </div>
      <div style={{ fontSize: "clamp(16px,1.9vw,28px)", fontStyle: "normal", fontWeight: 600, color: "#e8f4ff", fontFamily: FONT_DISPLAY, letterSpacing: 0.5, lineHeight: 1, whiteSpace: "nowrap" }}>
        <span style={{ color: accent }}>{meta.headerTitle}</span>
      </div>
    </div>

    <div style={S.body}>
      <div style={{ ...S.content, gap: "clamp(10px,1.2vw,16px)", justifyContent: "flex-start" }}>

        <div style={{ flexShrink: 0 }}>
          <div style={{ fontSize: "clamp(8px,0.68vw,11px)", fontFamily: FONT_MONO, color: "rgba(75,184,176,0.55)", letterSpacing: 3, marginBottom: "clamp(3px,0.35vw,5px)" }}>
            {meta.sanskrit}
          </div>
          <h1 style={{ fontFamily: FONT_DISPLAY, fontSize: "clamp(19px,2.3vw,30px)", fontStyle: "normal", fontWeight: 600, color: "#e8f4ff", margin: "0 0 clamp(4px,0.5vw,7px)", lineHeight: 1.15 }}>
            {meta.titlePre}<span style={{ color: accent }}>{meta.titleAccentWord}</span>{meta.titlePost}
          </h1>
          <p style={{ fontFamily: FONT_BODY, fontSize: "clamp(12px,1.0vw,15px)", fontStyle: "normal", fontWeight: 300, color: "#9bbdd6", margin: 0, lineHeight: 1.6 }}>
            {renderSegs(intro, "#ffffff")}
          </p>
        </div>

        <div style={{
          flex: 1, minHeight: 0,
          borderTop: "1px solid rgba(75,184,176,0.12)",
          borderBottom: "1px solid rgba(75,184,176,0.12)",
          paddingTop: "clamp(6px,0.8vw,10px)", paddingBottom: "clamp(4px,0.5vw,7px)",
          display: "flex", flexDirection: "column", justifyContent: "space-between", gap: "clamp(6px,0.7vw,10px)", overflowY: "hidden",
        }}>
          <div style={lbl}>{meta.techniqueLabel}</div>
          {technique.map((step, i) => (
            <div key={i} style={{ display: "flex", gap: "clamp(8px,0.9vw,13px)", alignItems: "flex-start", flexShrink: 0 }}>
              <span style={{ fontFamily: FONT_MONO, fontSize: "clamp(10px,0.8vw,12px)", color: accent, opacity: 0.60, flexShrink: 0, lineHeight: 1.6, minWidth: "1.8em" }}>
                {step.n}
              </span>
              <div style={{ minWidth: 0 }}>
                <span style={{ fontFamily: FONT_BODY, fontSize: "clamp(11px,0.92vw,14px)", fontStyle: "normal", fontWeight: 700, color: "#a8e6df" }}>
                  {step.title}&nbsp;
                </span>
                <span style={{ fontFamily: FONT_BODY, fontSize: "clamp(11px,0.9vw,14px)", fontStyle: "normal", fontWeight: 300, color: "#9bbdd6", lineHeight: 1.55 }}>
                  {step.desc}
                </span>
              </div>
            </div>
          ))}
        </div>

        <div style={{ flexShrink: 0, background: "rgba(75,184,176,0.06)", border: "1px solid rgba(75,184,176,0.22)", borderRadius: 8, padding: "clamp(8px,1vw,14px) clamp(11px,1.3vw,18px)" }}>
          <div style={lbl}>{meta.stillnessLabel}</div>
          <p style={{ fontFamily: FONT_BODY, fontSize: "clamp(12px,1.0vw,15px)", fontStyle: "normal", lineHeight: 1.6, color: "#a8e6df", margin: 0 }}>
            {renderSegs(stillness, "#ffffff")}
          </p>
        </div>

      </div>

      <div style={S.panel}>
        <MeditationGuide />
      </div>
    </div>

  </div>
 );
};

const MeditationGuideMobile: React.FC = () => {
  const { meta } = useMeditation();
  return (
  <div style={{ background:"#040f18", color:"#d0e8f8", fontFamily:font, height:"100%", width:"100%", boxSizing:"border-box", display:"flex", flexDirection:"column", padding:"18px 16px 20px", overflow:"hidden" }}>
    <div style={{ flexShrink:0, textAlign:"center", marginBottom:10 }}>
      <div style={{ fontFamily:FONT_MONO, fontSize:10, letterSpacing:2, color:"rgba(75,184,176,0.7)", textTransform:"uppercase", marginBottom:4 }}>{meta.breadcrumbPre}</div>
      <h1 style={{ fontFamily: FONT_DISPLAY, fontStyle:"normal", fontWeight:600, fontSize:22, margin:0, color:accent }}>{meta.headerTitle}</h1>
      <p style={{ fontFamily: FONT_BODY, fontStyle:"normal", fontWeight:300, fontSize:12.5, color:"#7aa8cc", margin:"3px 0 0" }}>{meta.guideSubtitle}</p>
    </div>
    <div style={{ flex:1, minHeight:0, width:"100%", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
      <MeditationGuide />
    </div>
  </div>
  );
};

const Meditation: React.FC = () => {
  const portrait = useIsPortrait();
  const mode = useMode();
  if (!portrait) return <ScaleToFit><MeditationInner /></ScaleToFit>;
  return mode === "practice" ? <MeditationGuideMobile /> : <MeditationMobile />;
};

export default Meditation;
